import React, { useCallback } from 'react';
import { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import {
    Grid,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Typography,
    Container,
    Card,
    CardContent,
    Chip,
    Avatar,
    Alert,
    CircularProgress,
    Divider,
    Badge,
    Tooltip,
    IconButton
} from '@mui/material';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
    Security as SecurityIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Download as DownloadIcon,
    LocationOn as LocationIcon,
    Business as BusinessIcon,
    Phone as PhoneIcon,
    Person as PersonIcon,
    Home as HomeIcon,
    Videocam as VideocamIcon,
    Monitor as MonitorIcon,
    Storage as StorageIcon,
    WifiOff as OfflineIcon,
    CheckCircle as OnlineIcon,
    Warning as WarningIcon,
    Build as MaintenanceIcon,
    Error as ErrorIcon,
    Visibility as ViewIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import DownloadTable from './download';
import { headerData } from '../../../data/headerCostum';
import DeleteData from './delete';
import DownloadRounded from '@mui/icons-material/DownloadRounded';

const defaultTheme = createTheme({
    palette: {
        primary: {
            main: '#e91e63',
        },
        secondary: {
            main: '#f48fb1',
        },
        background: {
            default: '#fdf2f8',
        },
    },
    typography: {
        h4: {
            fontWeight: 700,
        },
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
    },
    components: {
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        backgroundColor: 'white',
                        '&:hover fieldset': {
                            borderColor: '#e91e63',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#e91e63',
                        },
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    backgroundColor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e91e63',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e91e63',
                    },
                },
            },
        },
    },
});

const downloadColumns = [
    {
        id: 'kecamatan',
        label: 'Kecamatan',
    },
    {
        id: 'kelurahan',
        label: 'Kelurahan',
    },
    {
        id: 'nama_longwis',
        label: 'Nama Longwis',
    },
    {
        id: 'alamat',
        label: 'Alamat',
        minWidth: 150
    },
    {
        id: 'nama_pic',
        label: 'Nama PIC',
        minWidth: 100,
    },
    {
        id: 'kontak_pic',
        label: 'No Telpon',
        minWidth: 200,
    },
    {
        id: 'lokasi',
        label: 'Lokasi',
    },
    {
        id: 'status',
        parent: 'MonitoringCCTV',
        label: 'Status CCTV'
    },
    {
        id: 'ip_address',
        parent: 'MonitoringCCTV',
        label: 'IP Address'
    }
];

const columns = [
    {
        id: 'kecamatan',
        label: 'Kecamatan',
        minWidth: 120
    },
    {
        id: 'kelurahan',
        label: 'Kelurahan',
        minWidth: 120,
    },
    {
        id: 'nama_longwis',
        label: 'Nama Longwis',
        minWidth: 180,
    },
    {
        id: 'alamat',
        label: 'Alamat',
        minWidth: 150,
    },
    {
        id: 'nama_pic',
        label: 'Nama PIC',
        minWidth: 120,
    },
    {
        id: 'kontak_pic',
        label: 'No Telpon',
        minWidth: 130,
    },
    {
        id: 'MonitoringCCTV',
        label: 'Status CCTV',
        minWidth: 120,
        format: (value) => {
            if (!value) {
                return (
                    <Chip 
                        label="Belum Input" 
                        size="small" 
                        color="default" 
                        variant="outlined"
                    />
                );
            }

            const getStatusConfig = (status) => {
                switch (status?.toLowerCase()) {
                    case 'normal':
                        return { color: 'success', icon: <OnlineIcon sx={{ fontSize: 16 }} /> };
                    case 'rusak':
                        return { color: 'error', icon: <ErrorIcon sx={{ fontSize: 16 }} /> };
                    case 'maintenance':
                        return { color: 'warning', icon: <MaintenanceIcon sx={{ fontSize: 16 }} /> };
                    case 'offline':
                        return { color: 'error', icon: <OfflineIcon sx={{ fontSize: 16 }} /> };
                    default:
                        return { color: 'default', icon: <WarningIcon sx={{ fontSize: 16 }} /> };
                }
            };

            const config = getStatusConfig(value.status);
            
            return (
                <Chip
                    label={value.status || 'Unknown'}
                    size="small"
                    color={config.color}
                    icon={config.icon}
                    variant="filled"
                />
            );
        }
    },
    {
        id: 'MonitoringCCTV',
        label: 'IP Address',
        minWidth: 130,
        format: (value) => {
            if (!value || !value.ip_address) {
                return (
                    <Typography variant="body2" color="text.secondary">
                        Tidak Ada
                    </Typography>
                );
            }
            return (
                <Typography variant="body2" fontFamily="monospace">
                    {value.ip_address}
                </Typography>
            );
        }
    },
    {
        id: 'MonitoringCCTV',
        label: 'Foto CCTV',
        minWidth: 100,
        format: (value) => {
            if (value && value.foto_cctv) {
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <img 
                            width={50} 
                            height={50}
                            alt="CCTV" 
                            src={process.env.REACT_APP_API_URL + "uploads/monitoring-cctv/" + value.foto_cctv}
                            style={{ 
                                borderRadius: 4, 
                                border: '1px solid #ddd',
                                objectFit: 'cover'
                            }}
                        />
                    </Box>
                );
            } else {
                return (
                    <Chip 
                        label="Tidak Ada" 
                        size="small" 
                        color="error" 
                        variant="outlined"
                    />
                );
            }
        }
    },
    {
        id: 'MonitoringCCTV',
        label: 'Foto Monitor',
        minWidth: 100,
        format: (value) => {
            if (value && value.foto_layar) {
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <img 
                            width={50} 
                            height={50}
                            alt="Monitor" 
                            src={process.env.REACT_APP_API_URL + "uploads/monitoring-cctv/" + value.foto_layar}
                            style={{ 
                                borderRadius: 4, 
                                border: '1px solid #ddd',
                                objectFit: 'cover'
                            }}
                        />
                    </Box>
                );
            } else {
                return (
                    <Chip 
                        label="Tidak Ada" 
                        size="small" 
                        color="error" 
                        variant="outlined"
                    />
                );
            }
        }
    },
    {
        id: 'MonitoringCCTV',
        label: 'Foto NVR',
        minWidth: 100,
        format: (value) => {
            if (value && value.foto_nvr) {
                return (
                    <Box sx={{ textAlign: 'center' }}>
                        <img 
                            width={50} 
                            height={50}
                            alt="NVR" 
                            src={process.env.REACT_APP_API_URL + "uploads/monitoring-cctv/" + value.foto_nvr}
                            style={{ 
                                borderRadius: 4, 
                                border: '1px solid #ddd',
                                objectFit: 'cover'
                            }}
                        />
                    </Box>
                );
            } else {
                return (
                    <Chip 
                        label="Tidak Ada" 
                        size="small" 
                        color="error" 
                        variant="outlined"
                    />
                );
            }
        }
    },
    {
        id: 'lokasi',
        label: 'Lokasi',
        minWidth: 150,
        format: (value) => {
            if (!value) {
                return (
                    <Typography variant="body2" color="text.secondary">
                        Tidak Ada Lokasi
                    </Typography>
                );
            }
            
            try {
                const coords = JSON.parse(value);
                return (
                    <Typography variant="body2">
                        <a 
                            href={`https://maps.google.com/maps?ll=${coords[0]},${coords[1]}&z=15&t=m`}
                            target='_blank' 
                            rel="noreferrer"
                            style={{ color: '#e91e63', textDecoration: 'none' }}
                        >
                            {`lat: ${coords[0]}, long: ${coords[1]}`}
                        </a>
                    </Typography>
                );
            } catch (error) {
                return (
                    <Typography variant="body2" color="text.secondary">
                        Format Tidak Valid
                    </Typography>
                );
            }
        }
    },
];

export default function MonitoringCCTVAdmin() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Page Number
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    const [copyList, setCopyList] = useState(rows);

    // Filter
    const [kecamatanValue, setKecamatanValue] = useState('');
    const [statusValue, setStatusValue] = useState('');
    const [kecamatanData, setKecamatanData] = useState([]);
    const [searchValue, setSearchValue] = useState('');

    const statusOptions = ['Normal', 'Rusak', 'Maintenance', 'Offline'];

    const getDataListLongwis = useCallback(() => {
        setLoading(true);
        setError(null);
        
        fetch(process.env.REACT_APP_API_URL + "api/longwis", {
            method: 'get',
            headers: headerData
        })
            .then(res => {
                if (!res.ok) {
                    throw new Error('Gagal mengambil data monitoring CCTV');
                }
                return res.json();
            })
            .then(data => {
                const filteredData = data.filter((item) => item.MonitoringCCTV !== null);
                setRows(filteredData);
            })
            .catch(err => {
                console.error('Error fetching monitoring CCTV data:', err);
                setError(err.message || 'Terjadi kesalahan saat mengambil data');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [])

    const searchText = (searched) => {
        setSearchValue(searched);
        let filteredData = rows;
        
        // Apply search filter
        if (searched) {
            filteredData = filteredData.filter((item) =>
                item.nama_longwis.toUpperCase().includes(searched.toUpperCase()) ||
                item.kecamatan.toUpperCase().includes(searched.toUpperCase()) ||
                item.kelurahan.toUpperCase().includes(searched.toUpperCase()) ||
                item.nama_pic.toUpperCase().includes(searched.toUpperCase()) ||
                (item.MonitoringCCTV && item.MonitoringCCTV.ip_address && 
                 item.MonitoringCCTV.ip_address.toUpperCase().includes(searched.toUpperCase())) ||
                (item.MonitoringCCTV && item.MonitoringCCTV.status && 
                 item.MonitoringCCTV.status.toUpperCase().includes(searched.toUpperCase()))
            );
        }
        
        // Apply kecamatan filter
        if (kecamatanValue) {
            filteredData = filteredData.filter((item) =>
                item.kecamatan.toUpperCase().trim().includes(kecamatanValue.toUpperCase().trim())
            );
        }

        // Apply status filter
        if (statusValue) {
            filteredData = filteredData.filter((item) =>
                item.MonitoringCCTV && 
                item.MonitoringCCTV.status &&
                item.MonitoringCCTV.status.toUpperCase().trim().includes(statusValue.toUpperCase().trim())
            );
        }
        
        setCopyList(filteredData);
    }

    // Page handle
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    // Filter Handle
    const getKecamatanData = useCallback(() => {
        let kecamatan_list = [];
        rows.forEach((items) => {
            if (kecamatan_list.includes(items.kecamatan)) {
                return
            } else {
                kecamatan_list.push(items.kecamatan);
            }
        })
        setKecamatanData(kecamatan_list);
    }, [rows])

    const kecamatanHandle = useCallback((e) => {
        setKecamatanValue(e);
        applyFilters(searchValue, e, statusValue);
    }, [rows, searchValue, statusValue]);

    const statusHandle = useCallback((e) => {
        setStatusValue(e);
        applyFilters(searchValue, kecamatanValue, e);
    }, [rows, searchValue, kecamatanValue]);

    const applyFilters = (search, kecamatan, status) => {
        let filteredData = rows;
        
        // Apply search filter
        if (search) {
            filteredData = filteredData.filter((item) =>
                item.nama_longwis.toUpperCase().includes(search.toUpperCase()) ||
                item.kecamatan.toUpperCase().includes(search.toUpperCase()) ||
                item.kelurahan.toUpperCase().includes(search.toUpperCase()) ||
                item.nama_pic.toUpperCase().includes(search.toUpperCase()) ||
                (item.MonitoringCCTV && item.MonitoringCCTV.ip_address && 
                 item.MonitoringCCTV.ip_address.toUpperCase().includes(search.toUpperCase())) ||
                (item.MonitoringCCTV && item.MonitoringCCTV.status && 
                 item.MonitoringCCTV.status.toUpperCase().includes(search.toUpperCase()))
            );
        }
        
        // Apply kecamatan filter
        if (kecamatan) {
            filteredData = filteredData.filter((item) =>
                item.kecamatan.toUpperCase().trim().includes(kecamatan.toUpperCase().trim())
            );
        }

        // Apply status filter
        if (status) {
            filteredData = filteredData.filter((item) =>
                item.MonitoringCCTV && 
                item.MonitoringCCTV.status &&
                item.MonitoringCCTV.status.toUpperCase().trim().includes(status.toUpperCase().trim())
            );
        }
        
        setCopyList(filteredData);
    };

    const clearFilters = () => {
        setSearchValue('');
        setKecamatanValue('');
        setStatusValue('');
        setCopyList(rows);
    };

    // Stats calculation
    const getStats = () => {
        const totalWithCCTV = rows.filter(item => item.MonitoringCCTV).length;
        const normalCCTV = rows.filter(item => 
            item.MonitoringCCTV && item.MonitoringCCTV.status === 'Normal'
        ).length;
        const rusakCCTV = rows.filter(item => 
            item.MonitoringCCTV && item.MonitoringCCTV.status === 'Rusak'
        ).length;
        const maintenanceCCTV = rows.filter(item => 
            item.MonitoringCCTV && item.MonitoringCCTV.status === 'Maintenance'
        ).length;
        const offlineCCTV = rows.filter(item => 
            item.MonitoringCCTV && item.MonitoringCCTV.status === 'Offline'
        ).length;

        return { totalWithCCTV, normalCCTV, rusakCCTV, maintenanceCCTV, offlineCCTV };
    };

    const stats = getStats();

    useEffect(() => {
        if (rows.length === 0) {
            getDataListLongwis()
        } else {
            setCopyList(rows)
            getKecamatanData()
        }
    }, [getDataListLongwis, rows, getKecamatanData])

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: "linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)",
                py: 4
            }}
        >
            <ThemeProvider theme={defaultTheme}>
                <Container maxWidth="xl">
                    {/* Header Section */}
                    <Paper 
                        elevation={8}
                        sx={{
                            p: 4,
                            mb: 3,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.8) 100%)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.2)'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Box
                                sx={{
                                    width: 70,
                                    height: 70,
                                    borderRadius: '50%',
                                    backgroundColor: 'primary.main',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 3,
                                    boxShadow: 3
                                }}
                            >
                                <SecurityIcon sx={{ fontSize: 35, color: 'white' }} />
                            </Box>
                            
                            <Box sx={{ flex: 1 }}>
                                <Typography 
                                    component="h1" 
                                    variant="h4" 
                                    sx={{ 
                                        color: 'primary.main', 
                                        fontWeight: 700,
                                        mb: 0.5
                                    }}
                                >
                                    Data Monitoring CCTV
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        fontWeight: 500
                                    }}
                                >
                                    Kelola data monitoring CCTV lorong wisata
                                </Typography>
                            </Box>

                            {/* Stats Cards */}
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Card sx={{ minWidth: 100, textAlign: 'center' }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Typography variant="h6" color="primary.main" fontWeight={700}>
                                            {stats.totalWithCCTV}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Total CCTV
                                        </Typography>
                                    </CardContent>
                                </Card>
                                <Card sx={{ minWidth: 100, textAlign: 'center' }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Typography variant="h6" color="success.main" fontWeight={700}>
                                            {stats.normalCCTV}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Normal
                                        </Typography>
                                    </CardContent>
                                </Card>
                                <Card sx={{ minWidth: 100, textAlign: 'center' }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Typography variant="h6" color="error.main" fontWeight={700}>
                                            {stats.rusakCCTV}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Rusak
                                        </Typography>
                                    </CardContent>
                                </Card>
                                <Card sx={{ minWidth: 100, textAlign: 'center' }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Typography variant="h6" color="warning.main" fontWeight={700}>
                                            {stats.maintenanceCCTV}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Maintenance
                                        </Typography>
                                    </CardContent>
                                </Card>
                                <Card sx={{ minWidth: 100, textAlign: 'center' }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Typography variant="h6" color="error.main" fontWeight={700}>
                                            {stats.offlineCCTV}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Offline
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>

                        {/* Error Alert */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Loading Alert */}
                        {loading && (
                            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CircularProgress size={20} />
                                    Memuat data monitoring CCTV...
                                </Box>
                            </Alert>
                        )}
                    </Paper>

                    {/* Filters Section */}
                    <Paper 
                        elevation={4}
                        sx={{
                            p: 3,
                            mb: 3,
                            borderRadius: 3,
                            background: 'white'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <FilterIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6" fontWeight={600}>
                                Filter & Pencarian
                            </Typography>
                        </Box>

                        <Grid container spacing={3} alignItems="center">
                            {/* Search */}
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Pencarian"
                                    placeholder="Nama longwis, kecamatan, PIC, IP, atau status"
                                    value={searchValue}
                                    onChange={(e) => searchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                                    }}
                                />
                            </Grid>

                            {/* Kecamatan Filter */}
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Kecamatan</InputLabel>
                                    <Select
                                        value={kecamatanValue}
                                        label="Kecamatan"
                                        onChange={(e) => kecamatanHandle(e.target.value)}
                                        startAdornment={<BusinessIcon color="action" sx={{ mr: 1 }} />}
                                    >
                                        <MenuItem value="">
                                            <em>Semua Kecamatan</em>
                                        </MenuItem>
                                        {kecamatanData.map((items, index) => (
                                            <MenuItem key={index} value={items}>{items}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Status Filter */}
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status CCTV</InputLabel>
                                    <Select
                                        value={statusValue}
                                        label="Status CCTV"
                                        onChange={(e) => statusHandle(e.target.value)}
                                        startAdornment={<SecurityIcon color="action" sx={{ mr: 1 }} />}
                                    >
                                        <MenuItem value="">
                                            <em>Semua Status</em>
                                        </MenuItem>
                                        {statusOptions.map((status, index) => (
                                            <MenuItem key={index} value={status}>{status}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Actions */}
                            <Grid item xs={12} md={2}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={clearFilters}
                                        size="small"
                                        sx={{ px: 2 }}
                                    >
                                        Reset
                                    </Button>
                                    <DownloadTable 
                                        columns={downloadColumns} 
                                        rows={copyList} 
                                        filename={'data_MonitoringCCTV.csv'} 
                                    />
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Active Filters */}
                        {(searchValue || kecamatanValue || statusValue) && (
                            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                    Filter aktif:
                                </Typography>
                                {searchValue && (
                                    <Chip
                                        size="small"
                                        label={`Pencarian: ${searchValue}`}
                                        onDelete={() => {
                                            setSearchValue('');
                                            searchText('');
                                        }}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {kecamatanValue && (
                                    <Chip
                                        size="small"
                                        label={`Kecamatan: ${kecamatanValue}`}
                                        onDelete={() => kecamatanHandle('')}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {statusValue && (
                                    <Chip
                                        size="small"
                                        label={`Status: ${statusValue}`}
                                        onDelete={() => statusHandle('')}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        )}
                    </Paper>

                    {/* Table Section */}
                    <Paper 
                        elevation={8}
                        sx={{
                            borderRadius: 3,
                            overflow: 'hidden',
                            background: 'white'
                        }}
                    >
                        <TableContainer sx={{ maxHeight: '70vh' }}>
                            <Table size='small' stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                                fontWeight: 700,
                                                minWidth: 60
                                            }}
                                        >
                                            No
                                        </TableCell>
                                        {columns.map((column, index) => (
                                            <TableCell
                                                key={`${column.id}-${index}`}
                                                align={column.align || 'left'}
                                                sx={{
                                                    backgroundColor: 'primary.main',
                                                    color: 'white',
                                                    fontWeight: 700,
                                                    minWidth: column.minWidth
                                                }}
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                        <TableCell
                                            align="center"
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                color: 'white',
                                                fontWeight: 700,
                                                minWidth: 120
                                            }}
                                        >
                                            Aksi
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {copyList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => {
                                            const currentRowNumber = index + 1 + page * rowsPerPage;
                                            return (
                                                <TableRow 
                                                    hover 
                                                    role="checkbox" 
                                                    tabIndex={-1} 
                                                    key={row.id}
                                                    sx={{
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(233, 30, 99, 0.04)'
                                                        }
                                                    }}
                                                >
                                                    <TableCell align="center">
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {currentRowNumber}
                                                        </Typography>
                                                    </TableCell>
                                                    {columns.map((column, colIndex) => {
                                                        const value = row[column.id];
                                                        return (
                                                            <TableCell 
                                                                key={`${column.id}-${colIndex}`} 
                                                                align={column.align || 'left'}
                                                                sx={{ 
                                                                    py: 1,
                                                                    maxWidth: column.minWidth,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis'
                                                                }}
                                                            >
                                                                {column.format 
                                                                    ? column.format(value) 
                                                                    : (
                                                                        <Typography variant="body2">
                                                                            {value || '-'}
                                                                        </Typography>
                                                                    )
                                                                }
                                                            </TableCell>
                                                        );
                                                    })}
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                            <Tooltip title="Lihat Detail">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => {
                                                                        // Handle view detail
                                                                        console.log('View detail:', row);
                                                                    }}
                                                                >
                                                                    <ViewIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <DeleteData id={row.id} endpoint="monitoring-cctv" />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={copyList.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: 'grey.50'
                            }}
                        />
                    </Paper>
                </Container>
            </ThemeProvider>
        </Box>
    );
}
